import React, { createContext, useContext, useRef, ReactNode } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

interface SceneContextType {
    sceneRef: React.MutableRefObject<THREE.Scene | null>;
    rendererRef: React.MutableRefObject<THREE.WebGLRenderer | null>;
    cameraRef: React.MutableRefObject<THREE.PerspectiveCamera | null>;
    droneCameraRef: React.MutableRefObject<THREE.PerspectiveCamera | null>;
    controlsRef: React.MutableRefObject<OrbitControls | null>;
    componentsRef: React.MutableRefObject<Array<{
        mesh: THREE.Mesh;
        solidMaterial: THREE.Material;
        wireframeMaterial: THREE.Material;
    }>>;
    framePartsRef: React.MutableRefObject<Array<{
        mesh: THREE.Mesh;
        isCracked: boolean;
        offset: number;
    }>>;
    particlesRef: React.MutableRefObject<{
        mesh: THREE.Points;
        material: THREE.PointsMaterial;
    } | null>;
    droneRef: React.MutableRefObject<{
        group: THREE.Group;
        solidMaterial: THREE.Material;
        wireframeMaterial: THREE.Material;
        propellerMaterial: THREE.Material;
    } | null>;
    animationFrameId: React.MutableRefObject<number | null>;
}

const SceneContext = createContext<SceneContextType | undefined>(undefined);

export const useSceneContext = (): SceneContextType => {
    const context = useContext(SceneContext);
    if (!context) {
        throw new Error('useSceneContext must be used within a SceneProvider');
    }
    return context;
};

interface SceneProviderProps {
    children: ReactNode;
}

export const SceneProvider: React.FC<SceneProviderProps> = ({ children }) => {
    const sceneRef = useRef<THREE.Scene | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const droneCameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const controlsRef = useRef<OrbitControls | null>(null);
    const componentsRef = useRef<Array<{
        mesh: THREE.Mesh;
        solidMaterial: THREE.Material;
        wireframeMaterial: THREE.Material;
    }>>([]);
    const particlesRef = useRef<{
        mesh: THREE.Points;
        material: THREE.PointsMaterial;
    } | null>(null);
    const droneRef = useRef<{
        group: THREE.Group;
        solidMaterial: THREE.Material;
        wireframeMaterial: THREE.Material;
        propellerMaterial: THREE.Material;
    } | null>(null);
    const framePartsRef = useRef<Array<{
        mesh: THREE.Mesh;
        isCracked: boolean;
        offset: number;
    }>>([]);
    const animationFrameId = useRef<number | null>(null);

    const value: SceneContextType = {
        sceneRef,
        rendererRef,
        cameraRef,
        droneCameraRef,
        controlsRef,
        componentsRef,
        particlesRef,
        droneRef,
        framePartsRef,
        animationFrameId
    };

    return (
        <SceneContext.Provider value={value}>
            {children}
        </SceneContext.Provider>
    );
}; 